import { useEffect, useState } from "react";
import { api } from "../api";

export default function AdminReferralQr() {
  const [employeeName, setEmployeeName] = useState("");
  const [code, setCode] = useState("");
  const [items, setItems] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadReferrals() {
    try {
      const data = await api.adminReferralList();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e.message || "Failed to load referrals");
    }
  }

  useEffect(() => {
    loadReferrals();
  }, []);

  async function createReferral(e) {
    e.preventDefault();
    setMsg("");
    setQrData(null);

    const cleanName = employeeName.trim();
    const cleanCode = code.trim().toUpperCase();

    if (!cleanName || !cleanCode) {
      setMsg("Employee name and code are required.");
      return;
    }

    try {
      setBusy(true);

      await api.adminReferralCreate({
        employeeName: cleanName,
        code: cleanCode,
      });

      const qr = await api.adminReferralQr(cleanCode);
      setQrData(qr);

      setEmployeeName("");
      setCode("");

      await loadReferrals();

      setMsg("Referral QR created.");
    } catch (e) {
      setMsg(e.message || "Failed to create referral QR");
    } finally {
      setBusy(false);
    }
  }

  async function viewQr(refCode) {
    setMsg("");
    setQrData(null);

    try {
      setBusy(true);
      const qr = await api.adminReferralQr(refCode);
      setQrData(qr);
    } catch (e) {
      setMsg(e.message || "Failed to generate QR");
    } finally {
      setBusy(false);
    }
  }

  async function disableReferral(refCode) {
    setMsg("");

    if (!window.confirm(`Disable referral code ${refCode}?`)) return;

    try {
      setBusy(true);
      await api.adminReferralDisable(refCode);
      await loadReferrals();

      if (qrData?.code === refCode) {
        setQrData(null);
      }

      setMsg("Referral disabled.");
    } catch (e) {
      setMsg(e.message || "Failed to disable referral");
    } finally {
      setBusy(false);
    }
  }

  function downloadQr() {
    if (!qrData?.qr) return;

    const a = document.createElement("a");
    a.href = qrData.qr;
    a.download = `referral-${qrData.code}.png`;
    a.click();
  }

  return (
    <div className="page">
      <div className="card">
        <div className="cardHeader">
          <h2>Referral QR Codes</h2>
          <div className="smallNote">
            Create QR codes for employees/users and track scans, installs, and signups.
          </div>
        </div>

        <form onSubmit={createReferral} className="form">
          <label>Employee / User name</label>
          <input
            value={employeeName}
            onChange={(e) => setEmployeeName(e.target.value)}
            placeholder="Example: John"
          />

          <label>Referral code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Example: JOHN01"
          />

          <button className="btnPrimary" type="submit" disabled={busy}>
            {busy ? "Working..." : "Create QR"}
          </button>

          {msg ? <div className="msg">{msg}</div> : null}
        </form>
      </div>

      {qrData ? (
        <div className="card">
          <div className="cardHeader">
            <h3>{qrData.employeeName}</h3>
            <div className="smallNote">Code: {qrData.code}</div>
          </div>

          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <img
              src={qrData.qr}
              alt={`Referral QR ${qrData.code}`}
              style={{ width: 260, height: 260, borderRadius: 12 }}
            />

            <div>
              <div className="smallNote">QR link:</div>
              <div style={{ wordBreak: "break-all", marginBottom: 12 }}>
                {qrData.link}
              </div>

              <button className="btnPrimary" type="button" onClick={downloadQr}>
                Download QR
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="cardHeader">
          <h3>Existing Referral Codes</h3>
        </div>

        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Scans</th>
                <th>Installs</th>
                <th>Signups</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item._id || item.code}>
                  <td>{item.employeeName}</td>
                  <td>{item.code}</td>
                  <td>{item.active ? "Active" : "Disabled"}</td>
                  <td>{item.scans || 0}</td>
                  <td>{item.installs || 0}</td>
                  <td>{item.signups || 0}</td>
                  <td>
                    <button
                      type="button"
                      className="btnSecondary"
                      onClick={() => viewQr(item.code)}
                      disabled={busy}
                    >
                      View QR
                    </button>

                    {item.active ? (
                      <button
                        type="button"
                        className="btnDanger"
                        onClick={() => disableReferral(item.code)}
                        disabled={busy}
                        style={{ marginLeft: 8 }}
                      >
                        Disable
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}

              {!items.length ? (
                <tr>
                  <td colSpan="7">No referral codes yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}